import {
  SUPPORT_PHONE_NUMBER,
  WHATSAPP_SUPPORT_NUMBER,
} from '@common/constants'
import { CClient } from '@common/utils'
import AlertMessage from '@components/atoms/alert-message'
import RegistrationNavBar from '@components/auth/navbar'
import Countdown from '@components/common/countdown'
import { LazyImage } from '@components/common/image'
import Spinner from '@components/common/spinner'
import Background from '@components/molecules/background'
import { useSessionContext } from '@contexts/session'
import useDetectCountry from '@hooks/useDetectCountry'
import useSegment, { SegmentEventEnum } from '@hooks/useSegment'
import date, {
  formatTimeByTimezone,
  millisecondsToTime,
  SYSTEM_TIMEZONE,
  TIME_FORMAT_TZ,
} from '@lib/utils/date.utils'
import { captureMessage } from '@sentry/node'
import Link from 'next/link'
import { useRouter } from 'next/router'
import nookies from 'nookies'
import { useEffect, useState } from 'react'
import { Phone } from 'react-feather'
import PhoneInput from 'react-phone-input-2'
import swal from 'sweetalert'

type VerifyUserResponseType = {
  token: string
  batch: string
}

export type ClassroomDetailsType = {
  grade: string
  lessonTitle: string
  startTime: string
  teacherName: string
}

type Props = {
  classroomDetails?: ClassroomDetailsType
}

const IMAGE_PATH = '/images/short-link/'

const ShortLink = ({ classroomDetails }: Props) => {
  const track = useSegment()

  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { phone: currentUserPhone, loggedIn, timezone } = useSessionContext()
  const [phone, setPhone] = useState('')
  const [isEnterPhoneTracked, setIsEnterPhoneTracked] = useState(false)
  const router = useRouter()
  const { countryCode } = useDetectCountry({
    isLowerCase: true,
    isDeterminedViaSystemTimezoneOnly: true,
  })

  const handleVerifyPhone = async (currentPhoneNumber: string) => {
    /**
      tracking only if not logged in because when the user is logged in, he/she
      is automatically redirected to the class without clicking the CTA
    */
    if (!loggedIn) {
      track(
        SegmentEventEnum.TRIAL_SHORT_LINK_CLICK_JOIN_CLASS,
        classroomDetails
      )
    }

    setErrorMessage('')
    setIsLoading(true)

    const shortClassLinkId = router.query.slug as string
    try {
      const response = await CClient(
        `/api/v1/classroom/${shortClassLinkId}/verify/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            passcode: `+${currentPhoneNumber}`,
          }),
        }
      )
      if (response.ok) {
        track(SegmentEventEnum.TRIAL_SHORT_LINK_SUCCESSFUL_PHONE_VERIFICATION)
        const responseData: VerifyUserResponseType = await response.json()
        nookies.set({}, 'token', responseData.token, {
          maxAge: 24 * 60 * 60,
          path: '/',
        })
        router.replace(`/batches/${responseData.batch}/classroom/`)
      } else if (response.status === 404) {
        setIsLoading(false)
        setErrorMessage('Please enter a valid phone number.')
      } else if (response.status === 403) {
        if (loggedIn) {
          swal({
            icon: 'error',
            title: 'Sorry!',
            text:
              'Sorry! You can not access this class link. Please contact support for any help.',
          }).then(() => router.replace('/dashboard'))
        } else {
          setIsLoading(false)
          setErrorMessage('Please enter a valid phone number.')
        }
      } else {
        throw new Error(response.statusText)
      }
    } catch (error) {
      captureMessage(error)
      swal({
        icon: 'error',
        title: 'Sorry!',
        text:
          'Something went wrong. Please refresh the page and try again. If the error persists then please contact support.',
      })
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (loggedIn) handleVerifyPhone(currentUserPhone)
    track(SegmentEventEnum.TRIAL_SHORT_LINK_VISIT, classroomDetails)
  }, [])

  useEffect(() => {
    if (phone && !isEnterPhoneTracked) {
      track(SegmentEventEnum.TRIAL_SHORT_LINK_ENTER_PASSCODE, classroomDetails)
      setIsEnterPhoneTracked(true)
    }
  }, [phone])

  const checkedTimezone = timezone ? timezone : SYSTEM_TIMEZONE

  const difference = date(classroomDetails?.startTime).diff(date())

  const timeObject = millisecondsToTime(difference)

  return (
    <Background
      rootChildren={<RegistrationNavBar />}
      className="flex flex-col min-h-screen"
      contentClassName="max-w-6xl mx-auto my-10 flex justify-center px-3"
      basePath={`${IMAGE_PATH}bg/`}
      images={[
        {
          fileName: 'bg-left',
          className: 'left-5 w-auto h-full hidden lg:block',
        },
        {
          fileName: 'bg-right',
          className: 'right-5 top-10 hidden lg:block',
        },
      ]}
    >
      {!classroomDetails ? (
        <div className="flex items-center justify-center m-auto bg-white">
          <div className="flex border rounded border-grey-100">
            <div className="flex flex-col items-center p-6 rounded-md">
              <LazyImage
                path={IMAGE_PATH}
                name="error"
                alt="chrome logo"
                className="h-40 my-8"
              />
              <div className="flex flex-col h-full text-lg text-grey">
                <p>Sorry, this link has expired</p>
                <Link href="/">
                  <a className="flex justify-center mt-1 text-center btn text-orange">
                    Go to Home
                  </a>
                </Link>
              </div>
            </div>
            <div className="flex flex-col items-center p-6 rounded-md">
              <LazyImage
                path={IMAGE_PATH}
                name="support"
                alt="chrome logo"
                className="w-24 my-8"
              />
              <div className="flex flex-col justify-end h-full text-lg text-grey">
                <p>If you need help, call us on</p>
                <a
                  href={`tel:${SUPPORT_PHONE_NUMBER.value}`}
                  className="flex space-x-2 font-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Phone className="w-5" />
                  <span>{SUPPORT_PHONE_NUMBER.label}</span>
                </a>
                <p className="self-center">Or</p>
                <a
                  href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER.value}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex justify-center mt-1 text-center text-white bg-green-200 btn hover:bg-green-100"
                >
                  Message on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col m-auto">
          <div className="mb-4 text-center text-grey">
            <h1 className="text-3xl lg:text-5xl font-600">
              Welcome to the magical world of coding!
            </h1>
            <p className="text-xl">
              Your coding class is scheduled at{' '}
              <span className="font-600">
                {formatTimeByTimezone(
                  classroomDetails.startTime,
                  checkedTimezone,
                  TIME_FORMAT_TZ
                )}
              </span>
            </p>
          </div>
          <div className="p-6 text-center bg-white border rounded-md border-grey-400">
            <p className="px-2 mb-3 text-xl">{classroomDetails.lessonTitle}</p>
            <div className="flex items-center justify-center w-full pb-5">
              <Countdown startTime={{ ...timeObject, oneTenthMs: 0 }} />
            </div>
            <hr className="text-grey-400" />
            <div className="flex justify-center pt-5 space-x-32 text-left">
              <div>
                <div className="">Grade</div>
                <div className="text-lg font-600">{classroomDetails.grade}</div>
              </div>
              <div>
                <div className="">Teacher name</div>
                <div className="text-lg font-600">
                  {classroomDetails.teacherName}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center w-full mt-8 space-y-5">
            <div className="text-2xl font-600">
              Enter your phone number to enter the class
            </div>
            <div className="w-80">
              <PhoneInput
                containerClass="font-sans text-base leading-tight"
                inputClass="appearance-none border border-grey-500 rounded w-full py-2 pl-16 pr-4 text-grey leading-tight focus:outline-none"
                country={countryCode}
                value={phone}
                onChange={setPhone}
                onEnterKeyPress={() => handleVerifyPhone(phone)}
                masks={{ in: '..... .....' }}
                inputProps={{
                  id: 'phone',
                  name: 'phone',
                  required: true,
                  autoFocus: true,
                  placeholder: 'Phone number',
                }}
              />
            </div>
            {errorMessage && (
              <AlertMessage type="danger" icon="XCircle">
                {errorMessage}
              </AlertMessage>
            )}
            <button
              className={`btn btn-orange ${
                isLoading ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              disabled={isLoading}
              onClick={() => handleVerifyPhone(phone)}
            >
              {isLoading ? (
                <Spinner classes=" h-5 w-5" />
              ) : (
                <span>Join class</span>
              )}
            </button>
          </div>
        </div>
      )}
    </Background>
  )
}

export default ShortLink
